import React, { useRef, useState } from "react";
import {
  PlayCircleOutline as PlayIcon,
  MoreVert as MenuIcon,
  ErrorOutline as ErrorIcon,
} from "@mui/icons-material";
import makeStyles from "@mui/styles/makeStyles";
import { Menu, MenuItem, IconButton } from "@mui/material";

import useNexusVideoSession from "./useNexusVideoSession";

const useStyles = makeStyles((theme) => ({
  player: {
    maxWidth: "100%",
    width: "100%",
  },
  root: {
    margin: 0,
    position: "relative",
  },
  caption: {
    top: 0,
    position: "absolute",
    left: 0,
    right: 0,
    textAlign: "center",
    background:
      "linear-gradient(to bottom, rgba(0,0,0,0.85) 10%, rgba(0,0,0,0) 100%)",
    paddingBottom: theme.spacing(2),
    color: "#fff",
    display: "flex",
    alignItems: "center",
  },
  cationLabel: {
    flexGrow: 1,
  },
  overlay: {
    display: "grid",
    placeItems: "center",
    top: "30%",
    bottom: "30%",
    left: "35%",
    right: "35%",
    position: "absolute",
    textAlign: "center",
    background: "rgba(0,0,0,0.85)",
    borderRadius: theme.spacing(2),
    cursor: "pointer",
    zIndex: 1,
    color: "#fff",
    "&:hover": {
      background: `radial-gradient(#2e2e2e, rgba(0,0,0,0.85))`,
    },
  },
  playIcon: {
    width: "90%",
    height: "90%",
  },
  statusText: {
    fontSize: "0.85rem",
    marginTop: theme.spacing(1),
  },
  menuLeft: {
    width: "34px",
  },
}));

const LiveVideoPlayerNexusWebRtc = ({
  label,
  vehicleId,
  cameraId,
  onSetCameraLocationClick,
  onPlayClick,
  onStopClick,
  play,
}) => {
  const classes = useStyles();
  const videoRef = useRef(null);

  // The session is gone once it ends (30-min cap, ignition off), so "play again" cannot just flip
  // `play` — it is already true. Bumping this key re-runs the hook's effect and starts a new one.
  const [restartKey, setRestartKey] = useState(0);

  const { state, statusText } = useNexusVideoSession({
    mode: "live",
    vehicleId,
    cameraId,
    videoRef,
    play,
    restartKey,
  });

  const handleVideoPause = () => {
    if (videoRef.current?.srcObject) onStopClick?.();
  };

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);
  const handleMenuButtonClick = (event) => setMenuAnchorEl(event.currentTarget);
  const handleMenuClose = () => setMenuAnchorEl(null);
  const handleCameraLocationClick = () => {
    setMenuAnchorEl(null);
    onSetCameraLocationClick();
  };

  return (
    <figure className={classes.root}>
      {!play && (
        <div className={classes.overlay} onClick={onPlayClick}>
          <PlayIcon className={classes.playIcon} />
        </div>
      )}

      {play && state === "error" && (
        <div className={classes.overlay} onClick={onStopClick}>
          <div>
            <ErrorIcon style={{ fontSize: 48 }} />
            <div className={classes.statusText}>{statusText}</div>
          </div>
        </div>
      )}

      {play && state === "connecting" && (
        <div className={classes.overlay} onClick={onStopClick}>
          <div className={classes.statusText}>{statusText}</div>
        </div>
      )}

      {play && state === "idle" && (
        <div
          className={classes.overlay}
          onClick={() => setRestartKey((key) => key + 1)}
        >
          <div>
            <PlayIcon className={classes.playIcon} />
            <div className={classes.statusText}>{statusText}</div>
          </div>
        </div>
      )}

      <video
        className={classes.player}
        ref={videoRef}
        playsInline
        autoPlay
        muted
        controls
        onPause={handleVideoPause}
        onVolumeChange={(e) => {
          e.target.muted = true;
        }}
        poster="/live-video-poster.svg"
      >
        Sorry, your browser doesn't support embedded videos.
      </video>

      <figcaption className={classes.caption}>
        <div className={classes.menuLeft} />
        <div className={classes.cationLabel}>{label}</div>
        <div>
          <IconButton
            color="inherit"
            aria-controls={menuOpen ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? "true" : undefined}
            onClick={handleMenuButtonClick}
            size="small"
          >
            <MenuIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            MenuListProps={{ "aria-labelledby": "basic-button" }}
          >
            <MenuItem onClick={handleCameraLocationClick}>
              Rename camera
            </MenuItem>
            {play && <MenuItem onClick={() => onStopClick?.()}>Stop</MenuItem>}
          </Menu>
        </div>
      </figcaption>
    </figure>
  );
};

export default LiveVideoPlayerNexusWebRtc;
